import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Pencil from "../../../../images/pencil.png";
import { useForm } from "react-hook-form";
import { toast } from 'react-toastify';
import { useSelector } from "react-redux";
import { mapToOption } from "../../../../utlis";
import { useVendorForm } from "./../VendorFormContext";
import useAddressValidation from "../../../../hooks/useAddressValidation";
import AddressValidationDialog from "../../../../components/AddressValidationDialog";

const options = [
  { value: "", text: "--Choose an option--" },
  { value: "option1", text: "option1" },
  { value: "option2", text: "option2" },
  { value: "option3", text: "option3" },
  { value: "option4", text: "option4" },
  { value: "option5", text: "option5" },
];

const BillingAddress = () => {
  const navigate = useNavigate();
  const { formData, updateFormData, saveVendorData, saveAllChanges, hasUnsavedChanges, isCreating, isSaving, markTabComplete } = useVendorForm();

  const { register, watch, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: formData.billingInfo || {}, // Initialize with saved data
  });
  const referenceData = useSelector((state) => state.reference);
  const {
    validateAndProceed, isValidating, dialogState,
    acceptSuggestion, keepOriginal, saveAnyway, cancelDialog,
  } = useAddressValidation({
    fieldMap: { street: 'address_line1', street2: 'address_line2' },
    countryField: 'country',
  });
  const watchedData = watch(); // Watches all fields in the form

  const prevWatchedData = useRef(watchedData); // To track previous form state
  const isFirstSync = useRef(true);
  useEffect(() => {
    // Compare previous and current watched data
    // Skip first sync — resetVendorData already populated context
    if (JSON.stringify(prevWatchedData.current) !== JSON.stringify(watchedData)) {
      if (!isFirstSync.current) {
        updateFormData("billingInfo", watchedData);
      }
      prevWatchedData.current = watchedData;
      isFirstSync.current = false;
    }
  }, [watchedData, updateFormData]);

  const onSubmit = (formData) => {
    validateAndProceed(formData, async (validatedData) => {
      // Auto-fill form fields from USPS response
      if (validatedData.city !== formData.city) reset({ ...validatedData });
      if (validatedData.state !== formData.state) reset({ ...validatedData });
      if (!validatedData.country?.trim()) {
        validatedData.country = 'US';
      }
      // Block save if city or state is still empty
      if (!validatedData.city?.trim() || !validatedData.state?.trim()) {
        toast.error('City and State are required. Please validate your address or enter them manually.');
        return;
      }
      if (isCreating) {
        updateFormData("billingInfo", validatedData);
        markTabComplete("billing");
        navigate("/sales/vendors/details/company-profile");
      } else {
        await saveVendorData("billingInfo", { bill_to_address: validatedData });
        reset(validatedData);
      }
    });
  };

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-y-6 mt-5 text-sm font-normal text-text-hint">
        <div className="grid grid-cols-4 gap-3">
          <div className="">
            <label className="">
              Billing Contact Name<span className="text-danger-500">*</span>
            </label>
            <div className={`flex justify-center items-center px-3 py-2 mt-1 border rounded-lg ${errors.contact_name ? 'border-danger-500' : 'border-neutral-500'}`}>
              <input
                {...register("contact_name", { required: "Contact name is required", maxLength: { value: 50, message: "Contact name must not exceed 50 characters" } })}
                maxLength={50}
                placeholder="Billing Contact Name"
                className={`outline-none w-full text-black`}
              />
              <img src={Pencil} alt="pencil" />
            </div>
            {errors.contact_name && <span className="text-danger-500">{errors.contact_name.message}</span>}
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3">
          <div className="">
            <label className="">  
              Address line 1<span className="text-danger-500">*</span>
            </label>
            <div className={`flex justify-center items-center px-3 py-2 border rounded-lg  ${errors.address_line1 ? 'border-danger-500' : 'border-neutral-500 '}`}>
              <input
                {...register("address_line1", { required: "Address line 1 is required", maxLength: { value: 100, message: "Address must not exceed 100 characters" } })}
                placeholder="Address line 1"
                maxLength={100}
                className={`outline-none w-full text-black`}
              />
              <img src={Pencil} alt="pencil" />
            </div>
            {errors.address_line1 && <span className="text-danger-500">{errors.address_line1.message}</span>}
          </div>
          <div className="">
            <label className="">
              Address line 2
            </label>
            <div className="flex justify-center items-center px-3 py-2   border border-neutral-500 rounded-lg">
              <input
                {...register("address_line2", { maxLength: { value: 100, message: "Address must not exceed 100 characters" } })}
                placeholder="Address line 2"
                maxLength={100}
                className={`outline-none w-full text-black ${errors.address_line2 ? 'border-danger-500' : ''}`}
              />
              <img src={Pencil} alt="pencil" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3">
          <div className="">
            <label className="">
              City
            </label>
            <div className="flex justify-center items-center px-3 py-2 border border-neutral-500 rounded-lg">
              <input
                {...register("city")}
                placeholder="City"
                className="outline-none w-full text-black"
              />
              <img src={Pencil} alt="pencil" />
            </div>
          </div>
          <div className="">
            <label className="">
              State/Province
            </label>
            <select
              {...register("state")}
              className="flex justify-center items-center focus:ring-0 px-3 py-2 border rounded-lg outline-none w-full text-black border-neutral-500"
            >
              <option value="">-- Select State --</option>
              {mapToOption(referenceData.states?.state || [], 'name').map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.text}
                    </option>
                  ))}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3">
          <div className="">
            <label className="">
              Postal Code<span className="text-danger-500">*</span>
            </label>
            <div className="flex justify-center items-center px-3 py-2  border border-neutral-500 rounded-lg">
              <input
                {...register("zip", { required: "Postal code is required", maxLength: { value: 10, message: "Postal code must not exceed 10 characters" } })}
                placeholder="Postal Code"
                maxLength={10}
                className={`outline-none w-full text-black ${errors.zip ? 'border-danger-500' : ''}`}
              />
              <img src={Pencil} alt="pencil" />
            </div>
            {errors.zip && <span className="text-danger-500">{errors.zip.message}</span>}
          </div>
          <div className="">
            <label className="">
              Country/Region
            </label>
            <div className="flex justify-center items-center px-3 py-2 border border-neutral-500 rounded-lg">
              <input
                {...register("country")}
                placeholder="Country"
                className="outline-none w-full text-black"
              />
              <img src={Pencil} alt="pencil" />
            </div>
          </div>
        </div>
      </form>
      <div className="w-full py-10 px-8 flex justify-end items-center gap-2">
        <button
          type="button"
          onClick={() => navigate("/sales/vendors/summary/contact")}
          className="text-secondary-700 border border-secondary-700 py-2 px-5 rounded-full text-base bg-white hover:bg-gray-50 transition"
        >
          Previous
        </button>
        {!isCreating && hasUnsavedChanges && (
          <button
            type="button"
            disabled={isSaving}
            className="text-white bg-green-600 py-2 px-5 rounded-full text-base hover:bg-green-700 transition disabled:opacity-50"
            onClick={saveAllChanges}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        )}
        <button
          type="button"
          className="text-white bg-secondary-700 py-2 px-5 rounded-full text-base"
          onClick={handleSubmit((formData) => {
            if (isCreating) {
              updateFormData("billingInfo", formData);
              markTabComplete("billing");
            }
            navigate("/sales/vendors/details/company-profile");
          })}
        >
          Next
        </button>
      </div>

      <AddressValidationDialog
        dialogState={dialogState}
        onAcceptSuggestion={acceptSuggestion}
        onKeepOriginal={keepOriginal}
        onSaveAnyway={saveAnyway}
        onCancel={cancelDialog}
      />
    </div>
  );
};

export default BillingAddress;
